import { DownOutlined } from '@ant-design/icons';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Dropdown, Pagination } from 'antd';
import usePopUpAtOnce from 'components/hooks/usePopUpAtOnce';
import Loading from 'components/Loading';
import { filter, get, find } from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useAllBook } from 'store/books/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { useVideos } from 'store/resources/selectors';
import { useAllDistrictTeacher } from 'store/users/selectors';
import { useStory } from 'store/story/selectors';
import styled from 'styled-components';
import { ebookConfigDinamic } from 'utils/lib/ebookHelper';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '90vw', md: '80vw', lg: '70vw', xl: '60vw' },
  maxWidth: '1200px',
  minWidth: '320px',
  height: { xs: '80vh', sm: '75vh', md: '70vh', lg: '65vh', xl: '60vh' },
  maxHeight: '90vh',
  minHeight: '400px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0,
  borderRadius: '13px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

/* ----------------- styled components ----------------- */

const Wrapper = styled.div`
  border: 1px solid #d1d5db; /* gray-300 */
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  font-family: Arial, sans-serif;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: linear-gradient(90deg, #004aad, #cb6ce6);
  color: #fff;
  padding: 12px 16px;
  font-weight: 600;
  flex-shrink: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;

  th,
  td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb; /* gray-200 */
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 35%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 25%;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 20%;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 20%;
  }

  thead tr {
    background-color: #f9fafb; /* gray-50 */
  }

  tbody tr:hover {
    background-color: #f3f4f6; /* gray-100 */
  }

  a {
    color: #29528a;
    font-weight: 500;
    text-decoration: none;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 8px 12px;
      font-size: 14px;
    }

    th:nth-child(1),
    td:nth-child(1) {
      width: 40%;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 30%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 15%;
    }

    th:nth-child(4),
    td:nth-child(4) {
      width: 15%;
    }
  }

  @media (max-width: 480px) {
    th,
    td {
      padding: 6px 8px;
      font-size: 12px;
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  border-bottom: 1px solid #e5e7eb;
  min-height: 0;
  
  /* Ensure table doesn't interfere with dropdown scrollbars */
  .ant-dropdown {
    z-index: 1050;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  min-height: 72px;
  align-items: center;
`;

const ScrollableDropdownWrapper = styled.div`
  .scrollable-dropdown .ant-dropdown-menu {
    max-height: 100px;
    overflow-y: auto;
    overflow-x: hidden;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;

      &:hover {
        background: #a8a8a8;
      }
    }

    /* Hide scrollbar for Firefox */
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  }

  /* Ensure dropdown container doesn't have its own scrollbar */
  .scrollable-dropdown {
    overflow: visible !important;
  }

  /* Prevent any parent containers from adding scrollbars to dropdown */
  .ant-dropdown-menu-wrapper {
    overflow: visible !important;
  }

  /* Ensure only the menu itself has scrollbar */
  .ant-dropdown-menu {
    overflow-x: hidden !important;
  }
`;

const ShareByMe = ({ shareByMe, setShareByMe, data = [], redirectPage,from = '',loading=false }) => {
  const allDistrictTeacher = useAllDistrictTeacher();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const { updatePopUpAtOnce } = usePopUpAtOnce();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const booksAllList = useAllBook();
  const video = useVideos();
  const storyList = useStory();
  const currentClass = useCurrentClass();

  const findTeacherData = (id) => {
    const findShareBy = allDistrictTeacher.find((obj) => id === obj?.id);
    return findShareBy;
  };

  // Calculate paginated data
  const filteredData = useMemo(() => {
    return data.filter((obj) => {
      if (obj?.classId !== currentClass?.id) return false;
      const filterAccessBy = filter(obj?.share_list, (shareId) => shareId !== obj?.userId);
      return filterAccessBy && filterAccessBy.length > 0;
    });
  }, [data, currentClass?.id]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const handleDropdownOpenChange = (rowIndex, open) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [rowIndex]: open,
    }));
  };

  const handleDropdownClick = (event) => {
    event.stopPropagation();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClose = () => {
    setShareByMe({});
    updatePopUpAtOnce(false);
    setCurrentPage(1); // Reset to first page when closing
  };
  const findStoryDetails = useCallback((pageNumber) => {
    const pageNum = parseInt(pageNumber, 10);
    const storyPage = storyList.find((p) => {
      if (!Array.isArray(p.levels)) return false;
      return p.levels.some((level) => {
        const startNum = parseInt(level.start, 10);
        const finishNum = parseInt(level.finish, 10);
        if (isNaN(startNum) || isNaN(finishNum)) return false;
        return startNum <= pageNum && pageNum <= finishNum;
      });
    });
    if (!storyPage) return null;
    const matchingLevel = storyPage.levels.find((level) => {
      const startNum = parseInt(level.start, 10);
      const finishNum = parseInt(level.finish, 10);
      if (isNaN(startNum) || isNaN(finishNum)) return false;
      return startNum <= pageNum && pageNum <= finishNum;
    });

    if (!matchingLevel) return null;
    const startNum = parseInt(matchingLevel.start, 10);
    const relativePage = pageNum - startNum + 1;
    const title = storyPage?.title || "";
    const levelName = matchingLevel?.level || "";
    return { relativePage, title, levelName, pageNum };
  }, [storyList]);

  const getTitleAndLevel = useCallback(
    (pageNumber) => {
      const details = findStoryDetails(pageNumber);
      if (details) return `${details.title} ${details.levelName}`;
      return `${pageNumber}`;
    },
    [findStoryDetails],
  );

  const getType = (bookId) => {
    const findBook = booksAllList.find((obj) => bookId == obj?.id);
    return findBook?.type;
  };
  const getBookName = (bookId) => {
    if(from === 'videosection'){
      const findVideo = video.find((obj) => bookId == obj?.id);
      return findVideo?.displayName || "N/A";
    }
    const findBook = booksAllList.find((obj) => bookId == obj?.id);
    return findBook?.description;
  };

  const getNewMaskNote = useCallback((chapter, page) => {
    if (!chapter || !page) return page;
    const data = ebookConfigDinamic(chapter, booksAllList);
    const newMaskNote = get(
      find(get(data, 'content'), (k) => k.pageId == page),
      'numberMasked',
      page,
    );
    return newMaskNote;
  }, [booksAllList]);

  const getStoryRelativePageOnly = useCallback(
    (pageNumber) => {
      const details = findStoryDetails(pageNumber);
      if (details) return `${details.relativePage}`;
      return `${pageNumber}`;
    },
    [storyList],
  );


  return (
    <ScrollableDropdownWrapper>
      <Modal
        open={shareByMe}
        onClose={() => handleClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div style={{ position: 'absolute', top: 10, right: 10 }}>
            <CloseIcon
              onClick={() => {
                handleClose();
              }}
              style={{ cursor: 'pointer', fontSize: 20, color: 'red' }}
            />
          </div>
          <Wrapper>
            <Header>Shared by me</Header>
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>{from?.trim() === 'podcast ' ? 'Book/Pg. #' : 'Book'}</th>
                    <th style={{ textAlign: 'center' }}>Shared to</th>
                    <th style={{ textAlign: 'center' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center' }}>
                        <Loading size={23} />
                      </td>
                    </tr>
                  ) : filteredData?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: 'center',
                          padding: '40px 16px',
                          color: '#6c757d',
                          fontSize: '16px',
                          fontWeight: '500',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <div style={{ fontSize: '32px' }}>📤</div>
                          <div>NO DATA</div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#adb5bd',
                              fontWeight: '400',
                            }}
                          >
                            No shared content available
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedData?.map((row, i) => {
                      const filterAccessBy = filter(row?.share_list, (obj) => obj !== row?.userId);
                      const globalIndex = (currentPage - 1) * pageSize + i;
                      const isDropdownOpen = openDropdowns[globalIndex] || false;
                      const newMaskNote = getNewMaskNote(row?.chapter, row?.page);
                      const title = getType(row?.chapter) === 'story' 
                        ? getTitleAndLevel(row?.page)
                        : getBookName(row?.chapter);

                      return (
                        <tr key={globalIndex}>
                          <td>
                            <a onClick={() => redirectPage({ ...row, newMaskNote, title })} href="#">
                              # Notes {globalIndex + 1}
                            </a>
                          </td>
                          <td>
                          {getType(row?.chapter) === 'story'
                            ? `${getTitleAndLevel(row?.page)}/Pg.${getStoryRelativePageOnly(row?.page)}`
                            : from?.trim() === 'videosection'
                              ? getBookName(row?.chapter)
                              : `${getBookName(row?.chapter)}/Pg.${getNewMaskNote(row?.chapter, row?.page)}`}
                          </td>
                          <td>
                            <Dropdown
                              menu={{
                                items: filterAccessBy?.map((obj) => ({
                                  label: `${findTeacherData(obj)?.firstName} ${findTeacherData(obj)?.lastName}`,
                                  key: obj?.id,
                                })),
                              }}
                              onOpenChange={(open) => {
                                if (filterAccessBy?.length === 1) {
                                  return;
                                }
                                handleDropdownOpenChange(globalIndex, open);
                              }}
                              open={isDropdownOpen}
                              trigger={['click']}
                              optionRender={(item) => {
                                return <div>{item.label}</div>;
                              }}
                              overlayClassName="scrollable-dropdown-shared-with-me-by-me"
                            >
                              <div
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                                onClick={handleDropdownClick}
                              >
                                {findTeacherData(filterAccessBy[0])?.firstName}
                                {findTeacherData(filterAccessBy[0])?.lastName}
                                {filterAccessBy?.length > 1 && (
                                  <DownOutlined
                                    className={isDropdownOpen ? 'rotate-icon' : ''}
                                    style={{
                                      transition: 'transform 0.3s',
                                      transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                  />
                                )}
                              </div>
                            </Dropdown>
                          </td>
                          <td>
                          {row?.createdAt
                            ? moment(Number(row.createdAt).toString().length === 10 ? Number(row.createdAt) * 1000 : Number(row.createdAt)).format('MMMM Do, YYYY')
                            : row?.created_at
                            ? moment(Number(row.created_at).toString().length === 10 ? Number(row.created_at) * 1000 : Number(row.created_at)).format('MMMM Do, YYYY')
                            : ''}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </StyledTable>
            </TableContainer>
            {filteredData?.length > 0 && (
              <PaginationContainer>
                <Pagination
                  current={currentPage}
                  total={filteredData.length}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                />
              </PaginationContainer>
            )}
          </Wrapper>
        </Box>
      </Modal>
    </ScrollableDropdownWrapper>
  );
};

export default ShareByMe;
